<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import ConversationAPI from 'dashboard/api/inbox/conversation';

const props = defineProps({
  inboxId: {
    type: [String, Number],
    default: 0,
  },
  activeQueueFilter: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['queue-filter-change']);

const summary = ref(null);
const isLoading = ref(false);
let refreshTimer;

const items = [
  {
    key: 'waiting_count',
    label: 'CONVERSATION.LEGACY_QUEUE.OVERVIEW.WAITING',
    tone: 'bg-n-blue-3 text-n-blue-11',
    filter: 'waiting',
  },
  {
    key: 'online_waiting_count',
    label: 'CONVERSATION.LEGACY_QUEUE.OVERVIEW.ONLINE_WAITING',
    tone: 'bg-n-teal-3 text-n-teal-11',
    filter: 'online_waiting',
  },
  {
    key: 'offline_waiting_count',
    label: 'CONVERSATION.LEGACY_QUEUE.OVERVIEW.OFFLINE_WAITING',
    tone: 'bg-n-slate-3 text-n-slate-11',
    filter: 'offline_waiting',
  },
  {
    key: 'mine_waiting_count',
    label: 'CONVERSATION.LEGACY_QUEUE.OVERVIEW.MINE_WAITING',
    tone: 'bg-n-violet-3 text-n-violet-11',
    filter: 'mine_waiting',
  },
  {
    key: 'follow_up_count',
    label: 'CONVERSATION.LEGACY_QUEUE.OVERVIEW.FOLLOW_UP',
    tone: 'bg-n-violet-3 text-n-violet-11',
    filter: 'follow_up',
  },
  {
    key: 'team_follow_up_count',
    label: 'CONVERSATION.LEGACY_QUEUE.OVERVIEW.TEAM_FOLLOW_UP',
    tone: 'bg-n-indigo-3 text-n-indigo-11',
    filter: 'team_follow_up',
  },
  {
    key: 'team_follow_up_due_count',
    label: 'CONVERSATION.LEGACY_QUEUE.OVERVIEW.FOLLOW_UP_DUE',
    tone: 'bg-n-ruby-3 text-n-ruby-11',
    filter: 'team_follow_up_due',
  },
  {
    key: 'rescue_level_one_count',
    label: 'CONVERSATION.LEGACY_QUEUE.OVERVIEW.LEVEL_ONE',
    tone: 'bg-n-amber-3 text-n-amber-11',
    filter: 'rescue_level_one',
  },
  {
    key: 'rescue_level_two_count',
    label: 'CONVERSATION.LEGACY_QUEUE.OVERVIEW.LEVEL_TWO',
    tone: 'bg-n-ruby-3 text-n-ruby-11',
    filter: 'rescue_level_two',
  },
  {
    key: 'online_agent_count',
    label: 'CONVERSATION.LEGACY_QUEUE.OVERVIEW.ONLINE_AGENTS',
    tone: 'bg-n-teal-3 text-n-teal-11',
  },
];

const fetchSummary = async () => {
  if (isLoading.value) return;
  isLoading.value = true;
  try {
    const { data } = await ConversationAPI.legacyQueueSummary({
      inboxId: props.inboxId,
    });
    summary.value = data;
  } catch {
    // Keep the last successful snapshot; the next scheduled refresh will retry.
  } finally {
    isLoading.value = false;
  }
};

watch(() => props.inboxId, fetchSummary);

onMounted(() => {
  fetchSummary();
  refreshTimer = window.setInterval(fetchSummary, 30000);
});

onBeforeUnmount(() => window.clearInterval(refreshTimer));
</script>

<template>
  <section
    v-if="summary"
    class="border-b border-n-weak bg-n-surface-1 px-3 py-2"
    :aria-label="$t('CONVERSATION.LEGACY_QUEUE.OVERVIEW.TITLE')"
  >
    <div class="flex gap-1.5 overflow-x-auto">
      <button
        v-for="item in items"
        :key="item.key"
        type="button"
        class="flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xxs font-medium"
        :class="[
          item.tone,
          item.filter
            ? 'cursor-pointer hover:ring-1 hover:ring-current'
            : 'cursor-default',
          activeQueueFilter === item.filter ? 'ring-2 ring-current' : '',
        ]"
        :disabled="!item.filter"
        :aria-pressed="
          item.filter ? activeQueueFilter === item.filter : undefined
        "
        @click="item.filter && emit('queue-filter-change', item.filter)"
      >
        <span>{{ $t(item.label) }}</span>
        <strong class="font-semibold">{{ summary[item.key] || 0 }}</strong>
      </button>
    </div>
  </section>
</template>
